import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Flag,
  EyeOff,
  Eye,
  Trash2,
  UserX,
  Star,
  Store,
  Leaf,
  ExternalLink,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * ModerationTab — admin queue for user-generated review moderation.
 *
 * A review lands here when at least one user has clicked "Report" on it,
 * OR when it's been auto-hidden after crossing the flag threshold. Admin
 * can:
 *   • Approve (clears flags, un-hides)
 *   • Hide (force-hide even if not enough flags)
 *   • Delete (hard delete)
 *   • Ban user from reviews (hides ALL their reviews, blocks future ones)
 */

const STATUS_TABS = [
  { key: "flagged", label: "Flagged" },
  { key: "hidden", label: "Hidden" },
  { key: "all", label: "All" },
];

const KIND_ICON = {
  shop: Store,
  strain: Leaf,
};

const Stars = ({ value }) => (
  <div className="inline-flex items-center gap-0.5" aria-label={`${value} stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3 h-3 ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
        strokeWidth={0}
      />
    ))}
  </div>
);

const ReviewCard = ({ row, onAction }) => {
  const KindIcon = KIND_ICON[row.kind] || Flag;
  return (
    <div
      data-testid={`mod-review-${row.id}`}
      className="rounded-2xl border border-border bg-card p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <KindIcon className="w-3.5 h-3.5" />
            <span className="uppercase tracking-wider">{row.kind}</span>
            {row.target?.url ? (
              <a
                href={row.target.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground truncate"
              >
                {row.target.name || row.target.slug}
                <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            ) : (
              <span className="text-foreground/60 truncate">{row.target?.slug}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-sm font-medium">@{row.username || "unknown"}</span>
            <Stars value={row.rating || 0} />
            {row.is_hidden && (
              <Badge variant="destructive" className="text-[10px] uppercase tracking-wider">
                Hidden
              </Badge>
            )}
            {row.flag_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-200 border border-rose-500/30">
                <Flag className="w-3 h-3" />
                {row.flag_count} {row.flag_count === 1 ? "flag" : "flags"}
              </span>
            )}
          </div>
          {row.title && (
            <div className="font-serif text-base mt-3">{row.title}</div>
          )}
          {row.body && (
            <p className="text-sm text-foreground/85 mt-1 leading-relaxed whitespace-pre-wrap break-words">
              {row.body}
            </p>
          )}
          <div className="text-[10px] text-muted-foreground mt-3">
            {row.created_at && new Date(row.created_at).toLocaleString()}
            {row.updated_at && row.updated_at !== row.created_at && (
              <span> · edited {new Date(row.updated_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {row.is_hidden ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction("approve", row)}
              data-testid={`mod-approve-${row.id}`}
              className="h-8 rounded-full"
            >
              <Eye className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction("approve", row)}
              data-testid={`mod-clear-${row.id}`}
              className="h-8 rounded-full"
              title="Clear flags — false alarm"
            >
              <Eye className="w-3.5 h-3.5 mr-1" /> Clear flags
            </Button>
          )}
          {!row.is_hidden && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction("hide", row)}
              data-testid={`mod-hide-${row.id}`}
              className="h-8 rounded-full text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
            >
              <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
            </Button>
          )}
          <ConfirmButton
            label="Delete"
            icon={<Trash2 className="w-3.5 h-3.5 mr-1" />}
            title="Delete this review permanently?"
            description="This can't be undone. The review will be removed from the database."
            confirmLabel="Delete review"
            testId={`mod-delete-${row.id}`}
            variant="destructive"
            onConfirm={() => onAction("delete", row)}
          />
          <ConfirmButton
            label="Ban user"
            icon={<UserX className="w-3.5 h-3.5 mr-1" />}
            title={`Ban @${row.username || "user"} from reviews?`}
            description="They can still browse Bud Link but can't post new reviews, and every review they've written will be hidden from the public site."
            confirmLabel="Ban user"
            testId={`mod-ban-${row.id}`}
            variant="destructive"
            onConfirm={() => onAction("ban", row)}
          />
        </div>
      </div>
    </div>
  );
};

const ConfirmButton = ({ label, icon, title, description, confirmLabel, onConfirm, testId, variant = "default" }) => {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant={variant === "destructive" ? "outline" : "outline"}
          data-testid={testId}
          className={`h-8 rounded-full ${variant === "destructive" ? "text-rose-300 border-rose-500/40 hover:bg-rose-500/10" : ""}`}
        >
          {icon}
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-confirm`}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
            className={variant === "destructive" ? "bg-rose-600 hover:bg-rose-500" : ""}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const ModerationTab = () => {
  const [status, setStatus] = useState("flagged");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = (nextStatus = status) => {
    setLoading(true);
    api
      .get("/admin/reviews/moderation", { params: { status: nextStatus, kind: "all" } })
      .then((r) => setRows(r.data?.results || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleAction = async (action, row) => {
    try {
      if (action === "approve") {
        await api.post("/admin/reviews/approve", { kind: row.kind, review_id: row.id });
        toast.success("Review approved");
      } else if (action === "hide") {
        await api.post("/admin/reviews/hide", { kind: row.kind, review_id: row.id });
        toast.success("Review hidden");
      } else if (action === "delete") {
        await api.delete(`/admin/reviews/${row.kind}/${row.id}`);
        toast.success("Review deleted");
      } else if (action === "ban") {
        const res = await api.post("/admin/reviews/ban-user", { user_id: row.user_id });
        toast.success(`User banned · ${res.data?.hidden_reviews || 0} review(s) hidden`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl">Review moderation</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Reviews flagged by users or auto-hidden after 3 flags. Approve
            clears the flags · Hide removes it from the public site · Ban
            hides everything the user has ever written and blocks new posts.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-border p-1 bg-secondary/60">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`mod-tab-${t.key}`}
              className={`text-xs px-4 h-8 rounded-full transition-colors ${
                status === t.key
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4" data-testid="mod-loading">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="mod-empty"
          className="rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center"
        >
          <Flag className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="font-serif text-lg">Nothing to moderate</div>
          <p className="text-xs text-muted-foreground mt-1">
            {status === "flagged"
              ? "No flagged or hidden reviews right now — the community is behaving."
              : status === "hidden"
              ? "No hidden reviews."
              : "No native reviews yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="mod-list">
          {rows.map((row) => (
            <ReviewCard key={`${row.kind}:${row.id}`} row={row} onAction={handleAction} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationTab;
